import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { NEVER, Observable, fromEvent, merge, of } from 'rxjs';
import { delay, distinctUntilChanged, filter, shareReplay, startWith, switchMap, throttleTime } from 'rxjs/operators';
import { ExperimentalFeaturesFacade } from '../../user/facades';

@Injectable({
  providedIn: 'root',
})
export class IdleService {
  private readonly IDLE_TIMEOUT = 900 * 1000;
  private readonly THROTTLE_TIME = 1000;

  private idle$: Observable<boolean>;

  constructor(@Inject(PLATFORM_ID) private platform: Object, private experimentalFeaturesService: ExperimentalFeaturesFacade) {
    if (isPlatformBrowser(this.platform)) {
      const loaded$: Observable<Event> = NEVER.pipe(startWith(new Event('load')));
      const click$: Observable<Event> = fromEvent(document, 'click');
      const keyup$: Observable<Event> = fromEvent(document, 'keyup');
      const mousemove$: Observable<Event> = fromEvent(document, 'mousemove');
      const scroll$: Observable<Event> = fromEvent(document, 'scroll');
      const visibilitychange$: Observable<Event> = fromEvent(document, 'visibilitychange').pipe(filter(() => !document.hidden));

      this.idle$ = merge(loaded$, visibilitychange$, scroll$, click$, mousemove$, keyup$).pipe(
        throttleTime(this.THROTTLE_TIME),
        switchMap(() => of(true).pipe(delay(this.IDLE_TIMEOUT), startWith(false))),
        distinctUntilChanged(),
        shareReplay({ refCount: true, bufferSize: 1 })
      );
    } else {
      this.idle$ = NEVER.pipe(startWith(false));
    }
  }

  getIdle(): Observable<boolean> {
    return this.experimentalFeaturesService
      .hasIdleDetection()
      .pipe(switchMap((hasIdleDetection) => (hasIdleDetection ? this.idle$ : NEVER.pipe(startWith(false)))));
  }
}
